import React from "react";
import * as iboCartApi from "shared/api/iboCart";

// The IBO's own cart, as seen/edited from the Order Management area. This is one
// of the two separate carts in Create Order: items submitted here land in the
// IBO's real cart so the IBO checks out (and pays) themselves. The admin never
// checks this cart out. The sibling admin "New Order" cart lives in
// useOrderCart. Lifted to the OM parent so the selection/query stay shared.
//
// Two kinds of line items live here:
//   - The IBO's own items (addedByAdmin: false) seeded from selectedIbo.cart.
//     These are read-only to the rep — per Bret's directive the admin only
//     adds, never deletes. Only the IBO can remove their own items.
//   - Rep-added items (addedByAdmin: true). These start as `pending: true` —
//     a draft tray the rep can edit/remove freely. "Submit to Cart" commits
//     them (pending -> false), after which they're treated like committed
//     items and can no longer be removed by the rep.
// A line's identity for de-duplication: same product + color + size collapses
// into one cart line (quantities accumulate). Pending and committed lines are
// namespaced separately so a draft never clobbers an already-committed line's
// React key, but they share this signature so a re-add merges on submit.
const signatureOf = (productId, color, size) =>
  [productId, color, size].filter(Boolean).join("::");

const itemSignature = (item) =>
  signatureOf(
    item.productId ?? item.id ?? item.sku,
    item.selectedColor,
    item.selectedSize
  );

export function useIboCart(selectedIbo) {
  const [cartItems, setCartItems] = React.useState([]);

  // `submitPending` awaits the network before touching state, so it reads the
  // tray from a ref rather than closing over a snapshot that may be stale by
  // the time the push resolves.
  const cartItemsRef = React.useRef(cartItems);
  React.useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  // Re-seed the cart whenever the selected IBO changes so switching accounts
  // starts from that account's real cart.
  const seededIboRef = React.useRef(null);
  React.useEffect(() => {
    const iboKey = selectedIbo?.iboNumber ?? null;
    if (seededIboRef.current === iboKey) return;
    seededIboRef.current = iboKey;
    const seed = Array.isArray(selectedIbo?.cart) ? selectedIbo.cart : [];
    setCartItems(
      seed.map((item) => ({ ...item, addedByAdmin: false, pending: false }))
    );
  }, [selectedIbo]);

  const addToCart = React.useCallback((product, quantity = 1, selection) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    const color = selection?.color;
    const size = selection?.size;
    const variationId = selection?.variationId ?? null;
    const wasOverridden = selection?.wasOverridden || false;
    const productId = product.id ?? product.productId ?? product.sku;
    const sig = signatureOf(productId, color, size);
    // Namespace pending lines so they never collide with a committed/seeded
    // line for the same product (which would clash on React keys / selection).
    const lineId = `pending::${sig}`;
    setCartItems((prev) => {
      const existing = prev.find(
        (item) => item.lineId === lineId && item.pending
      );
      if (existing) {
        return prev.map((item) =>
          item.lineId === lineId && item.pending
            ? { ...item, qty: (item.qty || 1) + qty, wasOverridden }
            : item
        );
      }
      return [
        ...prev,
        {
          ...product,
          lineId,
          productId,
          variationId,
          qty,
          selectedColor: color,
          selectedSize: size,
          // Rep-added items start as an editable, not-yet-committed draft.
          addedByAdmin: true,
          pending: true,
          wasOverridden,
        },
      ];
    });
  }, []);

  const updateCartQty = React.useCallback((lineId, quantity) => {
    const qty = Math.max(1, Math.floor(Number(quantity)) || 1);
    setCartItems((prev) =>
      prev.map((item) =>
        item.lineId === lineId && item.pending ? { ...item, qty } : item
      )
    );
  }, []);

  // Remove any admin-added line (pending OR already committed). Per the design,
  // the admin can always pull back something they added, even after it was
  // submitted — but the IBO's own items (addedByAdmin: false) are never
  // removable from the admin side.
  const removeFromCart = React.useCallback((lineIds) => {
    const idSet = new Set(lineIds);
    setCartItems((prev) =>
      prev.filter((item) => !(item.addedByAdmin && idSet.has(item.lineId)))
    );
  }, []);

  // Commit the pending tray into the IBO's cart.
  //
  // The push is awaited BEFORE anything is promoted: a line only stops showing
  // as "Pending" once func-bsm-api has actually written it. Promoting first and
  // reconciling afterwards is what previously let a fully-skipped push read as
  // "Added by Admin" on screen while the member's cart was untouched.
  //
  // A promoted line that matches an already-committed admin line (same
  // product/color/size) merges into it and bumps its quantity rather than
  // creating a duplicate card. Resolves to the server result, annotated with
  // `committed` — the number of distinct pending lines that were promoted.
  const submitPending = React.useCallback(
    async (userId = null) => {
      const pendingToSubmit = cartItemsRef.current.filter((item) => item.pending);
      if (!pendingToSubmit.length) {
        return { committed: 0, accepted: [], skipped: [] };
      }

      const result = await iboCartApi.pushItems({
        iboId: selectedIbo?.id ?? selectedIbo?.iboNumber ?? null,
        userId: userId ?? null,
        items: pendingToSubmit.map((item) => ({
          sku: item.sku ?? null,
          variationId: item.variationId ?? null,
          // Colour/size travel with the line because the server needs both to
          // resolve a NetSuite variant to a sellable SQL variant row —
          // `variationId` alone is only the colour.
          color: item.selectedColor ?? null,
          size: item.selectedSize ?? null,
          quantity: item.qty || 1,
          sourceOrderId: item.sourceOrderId ?? null,
        })),
      });

      // A simulated push wrote nothing, so nothing may be promoted.
      if (result && result.simulated) {
        return { ...result, committed: 0 };
      }

      const acceptedIndexes = new Set(
        ((result && result.accepted) || [])
          .map((line) => line.lineIndex)
          .filter((i) => Number.isInteger(i))
      );
      const promotedLineIds = new Set(
        pendingToSubmit
          .filter((_, i) => acceptedIndexes.has(i))
          .map((item) => item.lineId)
      );

      let committed = 0;
      setCartItems((prev) => {
        const next = prev.map((item) => ({ ...item }));
        // Index existing committed admin lines by signature so re-adds merge.
        const committedAdminBySig = new Map();
        next.forEach((item) => {
          if (item.addedByAdmin && !item.pending) {
            committedAdminBySig.set(itemSignature(item), item);
          }
        });
        const out = [];
        next.forEach((item) => {
          if (!item.pending || !promotedLineIds.has(item.lineId)) {
            out.push(item);
            return;
          }
          committed += 1;
          const sig = itemSignature(item);
          const target = committedAdminBySig.get(sig);
          if (target) {
            target.qty = (target.qty || 1) + (item.qty || 1);
            return; // merged into the existing committed line; drop the draft
          }
          const promoted = {
            ...item,
            pending: false,
            lineId: `committed::${sig}`,
          };
          committedAdminBySig.set(sig, promoted);
          out.push(promoted);
        });
        return out;
      });

      return { ...result, committed, attempted: pendingToSubmit.length };
    },
    [selectedIbo]
  );

  const pendingItems = React.useMemo(
    () => cartItems.filter((item) => item.pending),
    [cartItems]
  );

  const pendingCount = React.useMemo(
    () => pendingItems.reduce((sum, item) => sum + (item.qty || 1), 0),
    [pendingItems]
  );

  const cartCount = React.useMemo(
    () => cartItems.reduce((sum, item) => sum + (item.qty || 1), 0),
    [cartItems]
  );

  return {
    cartItems,
    pendingItems,
    pendingCount,
    cartCount,
    addToCart,
    updateCartQty,
    removeFromCart,
    submitPending,
  };
}

export default useIboCart;
